import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";

import AppLayout from "@/components/AppLayout";
import LoginPage from "@/pages/LoginPage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import InitSetupPage from "@/pages/InitSetupPage";
import DashboardPage from "@/pages/DashboardPage";
import ApiKeysPage from "@/pages/ApiKeysPage";
import BillingPage from "@/pages/BillingPage";
import MemoryPage from "@/pages/MemoryPage";
import AdminsPage from "@/pages/AdminsPage";
import AuditPage from "@/pages/AuditPage";
import DataLakePage from "@/pages/DataLakePage";
import ProtocolsPage from "@/pages/ProtocolsPage";
import FieldLibraryPage from "@/pages/FieldLibraryPage";
import ChangePasswordPage from "@/pages/ChangePasswordPage";
import SettingsPage from "@/pages/SettingsPage";
import AgentsPage from "@/pages/AgentsPage";
import AgentEditorPage from "@/pages/AgentEditorPage";
import FlowsPage from "@/pages/FlowsPage";
import FlowEditorPage from "@/pages/FlowEditorPage";
import ObservabilityPage from "@/pages/ObservabilityPage";
import SharedTracePage from "@/pages/SharedTracePage";
import WebhookQueuePage from "@/pages/WebhookQueuePage";
import AdminTasksPage from "@/pages/AdminTasksPage";
import IngestJobsPage from "@/pages/IngestJobsPage";
import ConnectionsPage from "@/pages/ConnectionsPage";
import ConnectionAnalyticsPage from "@/pages/connections/ConnectionAnalyticsPage";
import ConnectionDetailPage from "@/pages/ConnectionDetailPage";
import CommonKnowledgePage from "@/pages/CommonKnowledgePage";
import TenantBoundaryPage from "@/pages/TenantBoundaryPage";
import IdeasPage from "@/pages/IdeasPage";
import S3FilesPage from "@/pages/S3FilesPage";
import CompliancePage from "@/pages/CompliancePage";
import RegionsPage from "@/pages/RegionsPage";
import RagPage from "@/pages/RagPage";
import CtasPage from "@/pages/CtasPage";
import BudgetsPage from "@/pages/BudgetsPage";
import StudyBuildsPage from "@/pages/StudyBuildsPage";
import PricingMemoryPage from "@/pages/PricingMemoryPage";
import ClientsPage from "@/pages/ClientsPage";
import ApiReferencePage from "@/pages/ApiReferencePage";
import GeneratedFormsPage from "@/pages/GeneratedFormsPage";
import GeneratedFormDetailPage from "@/pages/GeneratedFormDetailPage";
import PatientSchedulingPage from "@/pages/PatientSchedulingPage";
import EnrollmentDetailPage from "@/pages/EnrollmentDetailPage";
import IngestFilesPage from "@/pages/IngestFilesPage";
import QaFormGenerationPage from "@/pages/QaFormGenerationPage";
import QaBdFunnelPage from "@/pages/QaBdFunnelPage";
import QaRipenessRadarPage from "@/pages/QaRipenessRadarPage";
import QaValueAssumptionsPage from "@/pages/QaValueAssumptionsPage";
import QaChatPage from "@/pages/QaChatPage";
import FeatureRequestsListPage from "@/pages/FeatureRequestsListPage";
import FeatureRequestSubmitPage from "@/pages/FeatureRequestSubmitPage";
import FeatureRequestDetailPage from "@/pages/FeatureRequestDetailPage";
import IntegrationSecretsPage from "@/pages/IntegrationSecretsPage";
import PublicFeatureRequestSubmitPage from "@/pages/PublicFeatureRequestSubmitPage";
import PublicFeatureRequestThanksPage from "@/pages/PublicFeatureRequestThanksPage";
import LlmKeysPage from "@/pages/LlmKeysPage";
import SharedLlmProvidersPage from "@/pages/SharedLlmProvidersPage";
import RadarSyncPage from "@/pages/RadarSyncPage";
import DataLakeContextsPage from "@/pages/DataLakeContextsPage";
import SiteCentricAgentPage from "@/pages/SiteCentricAgentPage";
import ScheduledTasksPage from "@/pages/ScheduledTasksPage";
import ReleaseNotesPage from "@/pages/ReleaseNotesPage";
import MongoMcpSettingsPage from "@/pages/MongoMcpSettingsPage";
import BuildCheckPage from "@/pages/BuildCheckPage";
import AskDatabasePage from "@/pages/AskDatabasePage";
import { FrApiProvider } from "@/lib/frApi";

import "@/App.css";

function RequireAuth({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white/40 font-mono text-xs">
                Authenticating…
            </div>
        );
    }
    if (user === false) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    // 21 CFR Part 11 — force password change before letting the user do anything
    if (user.must_change_password && location.pathname !== "/change-password") {
        return <Navigate to="/change-password" replace />;
    }
    return children;
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/accept-invite" element={<AcceptInvitePage />} />
                    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                    <Route path="/reset-password" element={<ResetPasswordPage />} />
                    <Route path="/init" element={<InitSetupPage />} />
                    <Route path="/share/trace/:token" element={<SharedTracePage />} />
                    {/* Public, unauthenticated feature-request submission. */}
                    <Route path="/submit-feature-request" element={<PublicFeatureRequestSubmitPage />} />
                    <Route path="/submit-feature-request/thanks" element={<PublicFeatureRequestThanksPage />} />
                    <Route
                        path="/change-password"
                        element={
                            <RequireAuth>
                                <ChangePasswordPage />
                            </RequireAuth>
                        }
                    />
                    <Route
                        path="/"
                        element={
                            <RequireAuth>
                                <AppLayout />
                            </RequireAuth>
                        }
                    >
                        <Route index element={<DashboardPage />} />
                        <Route path="keys" element={<ApiKeysPage />} />
                        <Route path="billing" element={<BillingPage />} />
                        <Route path="memory" element={<MemoryPage />} />
                        <Route path="datalake" element={<DataLakePage />} />
                        <Route path="qa/protocols" element={<ProtocolsPage qaMode />} />
                        <Route path="qa/ctas" element={<CtasPage qaMode />} />
                        <Route path="qa/budgets" element={<BudgetsPage qaMode />} />
                        <Route path="qa/study-builds" element={<StudyBuildsPage qaMode />} />
                        <Route path="study-builds" element={<StudyBuildsPage />} />
                        <Route path="ask-database" element={<AskDatabasePage />} />
                        <Route path="qa/ingest-files" element={<IngestFilesPage />} />
                        <Route path="qa/edc-guidelines" element={<Navigate to="/qa/ingest-files" replace />} />
                        <Route path="qa/form-generation" element={<QaFormGenerationPage />} />
                        <Route path="qa/bd-funnel" element={<QaBdFunnelPage />} />
                        <Route path="qa/ripeness-radar" element={<QaRipenessRadarPage />} />
                        <Route path="qa/value-assumptions" element={<QaValueAssumptionsPage />} />
                        <Route path="qa/chat" element={<QaChatPage />} />
                        <Route path="pricing-memory" element={<PricingMemoryPage />} />
                        <Route path="clients" element={<ClientsPage />} />
                        <Route path="developer-reference" element={<ApiReferencePage />} />
                        <Route path="library" element={<FieldLibraryPage />} />
                        <Route path="forms" element={<GeneratedFormsPage />} />
                        {/* Legacy per-form curation detail (pre-Phase-3) — kept reachable via direct URL for back-compat */}
                        <Route path="forms/legacy/:id" element={<GeneratedFormDetailPage />} />
                        <Route path="scheduling" element={<PatientSchedulingPage />} />
                        <Route path="scheduling/:id" element={<EnrollmentDetailPage />} />
                        <Route path="agents" element={<AgentsPage />} />
                        <Route path="agents/:id" element={<AgentEditorPage />} />
                        <Route path="flows" element={<FlowsPage />} />
                        <Route path="flows/:id" element={<FlowEditorPage />} />
                        <Route path="admins" element={<AdminsPage />} />
                        <Route path="audit" element={<AuditPage />} />
                        <Route path="observability" element={<ObservabilityPage />} />
                        <Route path="webhook-queue" element={<WebhookQueuePage />} />
                        <Route path="tasks" element={<AdminTasksPage />} />
                        <Route path="ingest-jobs" element={<IngestJobsPage />} />
                        <Route path="connections" element={<ConnectionsPage />} />
                        <Route path="connections/_analytics" element={<ConnectionAnalyticsPage />} />
                        <Route path="connections/:id" element={<ConnectionDetailPage />} />
                        <Route path="scheduled-tasks" element={<ScheduledTasksPage />} />
                        <Route path="common-knowledge" element={<CommonKnowledgePage />} />
                        <Route path="tenant-boundary" element={<TenantBoundaryPage />} />
                        <Route path="ideas" element={<IdeasPage />} />
                        <Route path="s3-files" element={<S3FilesPage />} />
                        <Route path="compliance" element={<CompliancePage />} />
                        <Route path="regions" element={<RegionsPage />} />
                        <Route path="rag" element={<RagPage />} />
                        <Route path="settings" element={<SettingsPage />} />
                        <Route path="settings/llm-keys" element={<LlmKeysPage />} />
                        <Route path="settings/shared-llm" element={<SharedLlmProvidersPage />} />
                        <Route path="settings/radar-sync" element={<RadarSyncPage />} />
                        <Route path="settings/datalake-contexts" element={<DataLakeContextsPage />} />
                        <Route path="settings/sitecentric-agent" element={<SiteCentricAgentPage />} />
                        <Route path="settings/mongodb-mcp" element={<MongoMcpSettingsPage />} />
                        <Route path="release-notes" element={<ReleaseNotesPage />} />
                        <Route path="build-check" element={<BuildCheckPage />} />
                        <Route path="feature-requests" element={<FeatureRequestsListPage />} />
                        <Route path="feature-requests/submit" element={
                            <FrApiProvider mode="admin"><FeatureRequestSubmitPage /></FrApiProvider>
                        } />
                        <Route path="feature-requests/integration-secrets" element={<IntegrationSecretsPage />} />
                        <Route path="feature-requests/:id" element={<FeatureRequestDetailPage />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
            </BrowserRouter>
            <Toaster position="top-right" />
        </AuthProvider>
    );
}

export default App;
